/**
 * File-based Database for Maintenance Schedule Data
 * Uses localStorage for persistence
 */

const DB_NAME = 'maintenance_db';
const DB_VERSION = '1.0';

/**
 * Initialize the database
 */
export const initMaintenanceDB = () => {
  try {
    const existing = localStorage.getItem(DB_NAME);
    if (!existing) {
      const initialDB = {
        _version: DB_VERSION,
        _createdAt: new Date().toISOString(),
        _updatedAt: new Date().toISOString(),
        equipment: []
      };
      localStorage.setItem(DB_NAME, JSON.stringify(initialDB));
      return initialDB;
    }
    return JSON.parse(existing);
  } catch (e) {
    console.error('Error initializing maintenance database:', e);
    return {
      _version: DB_VERSION,
      _createdAt: new Date().toISOString(),
      _updatedAt: new Date().toISOString(),
      equipment: []
    };
  }
};

/**
 * Get all equipment
 */
export const getAllEquipment = () => {
  try {
    const db = initMaintenanceDB();
    return db.equipment || [];
  } catch (e) {
    console.error('Error getting all equipment:', e);
    return [];
  }
};

/**
 * Get equipment by ID
 */
export const getEquipmentById = (id) => {
  try {
    const allEquipment = getAllEquipment();
    return allEquipment.find(eq => eq.id === id) || null;
  } catch (e) {
    console.error('Error getting equipment by ID:', e);
    return null;
  }
};

/**
 * Save or update equipment
 */
export const saveEquipment = (equipmentData) => {
  try {
    const db = initMaintenanceDB();
    const allEquipment = db.equipment || [];
    
    const existingIndex = allEquipment.findIndex(eq => eq.id === equipmentData.id);
    
    const equipment = {
      ...equipmentData,
      updatedAt: new Date().toISOString()
    };
    
    if (existingIndex >= 0) {
      // Update existing
      equipment.createdAt = allEquipment[existingIndex].createdAt;
      allEquipment[existingIndex] = equipment;
    } else {
      // Insert new
      equipment.id = equipment.id || generateId();
      equipment.createdAt = new Date().toISOString();
      allEquipment.push(equipment);
    }
    
    db.equipment = allEquipment;
    db._updatedAt = new Date().toISOString();
    
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return equipment;
  } catch (e) {
    console.error('Error saving equipment:', e);
    throw e;
  }
};

/**
 * Delete equipment
 */
export const deleteEquipment = (id) => {
  try {
    const db = initMaintenanceDB();
    const allEquipment = db.equipment || [];
    
    db.equipment = allEquipment.filter(eq => eq.id !== id);
    db._updatedAt = new Date().toISOString();
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return true;
  } catch (e) {
    console.error('Error deleting equipment:', e);
    return false;
  }
};

/**
 * Clear all equipment from database
 */
export const clearAllEquipment = () => {
  try {
    const db = initMaintenanceDB();
    db.equipment = [];
    db._updatedAt = new Date().toISOString();
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return true;
  } catch (e) {
    console.error('Error clearing all equipment:', e);
    return false;
  }
};

/**
 * Record a maintenance service
 */
export const recordService = (equipmentId, serviceData) => {
  try {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) {
      throw new Error('Equipment not found');
    }
    
    const service = {
      id: generateId(),
      date: new Date().toISOString(),
      meterReading: serviceData.meterReading || equipment.currentMeterReading,
      notes: serviceData.notes || '',
      ...serviceData
    };
    
    equipment.services = equipment.services || [];
    equipment.services.push(service);
    
    // Update last service info
    equipment.lastServiceDate = service.date;
    equipment.lastServiceMeterReading = service.meterReading;
    equipment.currentMeterReading = service.meterReading;
    
    // Update daily usage if provided
    if (serviceData.dailyUsage !== undefined) {
      equipment.dailyUsage = serviceData.dailyUsage;
    }
    
    return saveEquipment(equipment);
  } catch (e) {
    console.error('Error recording service:', e);
    throw e;
  }
};

/**
 * Update current meter reading
 */
export const updateMeterReading = (equipmentId, meterReading) => {
  try {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) {
      throw new Error('Equipment not found');
    }
    
    equipment.currentMeterReading = meterReading;
    equipment.lastMeterUpdate = new Date().toISOString();
    
    return saveEquipment(equipment);
  } catch (e) {
    console.error('Error updating meter reading:', e);
    throw e;
  }
};

/**
 * Update daily usage
 */
export const updateDailyUsage = (equipmentId, dailyUsage) => {
  try {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) {
      throw new Error('Equipment not found');
    }
    
    equipment.dailyUsage = dailyUsage;
    equipment.lastUsageUpdate = new Date().toISOString();
    
    return saveEquipment(equipment);
  } catch (e) {
    console.error('Error updating daily usage:', e);
    throw e;
  }
};

/**
 * Calculate next service due dates
 */
export const calculateNextServiceDue = (equipment) => {
  const now = new Date();
  const results = {
    timeBased: null,
    meterBased: null,
    nextDue: null,
    daysUntilDue: null,
    status: 'ok' // 'ok', 'due_soon', 'due', 'overdue'
  };
  
  if (!equipment) return results;
  
  // Calculate time-based next service
  if (equipment.serviceIntervalDays && equipment.lastServiceDate) {
    const lastService = new Date(equipment.lastServiceDate);
    // Check if date is valid
    if (!isNaN(lastService.getTime())) {
      const nextServiceTime = new Date(lastService);
      nextServiceTime.setDate(nextServiceTime.getDate() + equipment.serviceIntervalDays);
      // Check if the calculated date is valid before converting to ISO
      if (!isNaN(nextServiceTime.getTime())) {
        results.timeBased = nextServiceTime.toISOString();
      }
    }
  }
  
  // Calculate meter-based next service
  if (equipment.serviceIntervalMeter && equipment.lastServiceMeterReading !== undefined) {
    const nextServiceMeter = equipment.lastServiceMeterReading + equipment.serviceIntervalMeter;
    results.meterBased = nextServiceMeter;
  }
  
  // Determine which comes first
  if (results.timeBased && results.meterBased) {
    const timeDate = new Date(results.timeBased);
    if (!isNaN(timeDate.getTime())) {
      const currentMeter = equipment.currentMeterReading || equipment.lastServiceMeterReading || 0;
      const daysUntilMeter = equipment.dailyUsage > 0 
        ? Math.ceil((results.meterBased - currentMeter) / equipment.dailyUsage)
        : Infinity;
      const meterDate = new Date(now);
      meterDate.setDate(meterDate.getDate() + daysUntilMeter);
      
      if (!isNaN(meterDate.getTime())) {
        results.nextDue = timeDate < meterDate ? results.timeBased : meterDate.toISOString();
      } else {
        results.nextDue = results.timeBased;
      }
    }
  } else if (results.timeBased) {
    const timeDate = new Date(results.timeBased);
    if (!isNaN(timeDate.getTime())) {
      results.nextDue = results.timeBased;
    }
  } else if (results.meterBased) {
    const currentMeter = equipment.currentMeterReading || equipment.lastServiceMeterReading || 0;
    const daysUntilMeter = equipment.dailyUsage > 0 
      ? Math.ceil((results.meterBased - currentMeter) / equipment.dailyUsage)
      : Infinity;
    const meterDate = new Date(now);
    meterDate.setDate(meterDate.getDate() + daysUntilMeter);
    if (!isNaN(meterDate.getTime())) {
      results.nextDue = meterDate.toISOString();
    }
  }
  
  // Calculate days until due
  if (results.nextDue) {
    const nextDueDate = new Date(results.nextDue);
    if (!isNaN(nextDueDate.getTime())) {
      const diffTime = nextDueDate - now;
      results.daysUntilDue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Determine status
      if (results.daysUntilDue < 0) {
        results.status = 'overdue';
      } else if (results.daysUntilDue === 0) {
        results.status = 'due';
      } else if (results.daysUntilDue <= 7) {
        results.status = 'due_soon';
      }
    } else {
      // Invalid date, reset nextDue
      results.nextDue = null;
    }
  }
  
  return results;
};

/**
 * Generate unique ID
 */
const generateId = () => {
  return `eq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

